import { graphql } from 'gatsby';
import React from 'react';
import Layout from 'components/Layout';
import SignpostContentWrapper from 'components/SignpostContent';
import { SignpostContentProps } from 'components/SignpostContent/SignpostContent';
import { HeroWithMediaData } from 'components/blocks/HeroBanner';
import HeroWithMedia from 'components/blocks/HeroBanner/HeroWithMedia';
import mapBlocks from 'helpers/blockMappers/mapBlocks';
import { CsBlock } from 'helpers/blockMappers/mappingUtils';
import { processPageMeta, unwrapSingleton } from 'helpers/csTypeProcessors';
import useLivePreviewSetup from 'helpers/useLivePreviewSetup';
import usePaginationNavigationState from 'helpers/usePaginationNavigationState';
import { CsEntryNode, PageMeta } from 'types/contentStack';

type Props = {
  data: TemplateAPageData;
};

const TemplateAPage: React.FC<Props> = ({ data }) => {
  const liveData = useLivePreviewSetup<TemplateAPageData['csTemplateA']>(
    data.csTemplateA
  );

  const page = liveData || data.csTemplateA;
  const defaultOgType = 'Info Page';
  const fallbackOgImage = page.hero_with_media?.image;
  const { meta, breadcrumbs } = processPageMeta(
    page.page_meta,
    page.url,
    data.allCsTemplateA.nodes,
    defaultOgType,
    fallbackOgImage
  );

  const signpostContent = unwrapSingleton(page.signpost_content);
  const currentPage = usePaginationNavigationState(page.blocks);

  return (
    <Layout
      url={page.url}
      pageMetadata={meta}
      breadcrumbs={breadcrumbs}
      locale={page.locale}
      disableGlobalNotification={!!signpostContent}>
      <SignpostContentWrapper signpostContent={signpostContent}>
        <HeroWithMedia
          hero_with_media={page.hero_with_media}
          showResponsiveVersion={page.show_new_hero}
        />
        {page.blocks &&
          mapBlocks(page.blocks, {
            pageMeta: page.page_meta,
            pageUrl: page.url,
            currentPage: currentPage,
          })}
      </SignpostContentWrapper>
    </Layout>
  );
};

export default TemplateAPage;

type TemplateAPageData = {
  csTemplateA: {
    cslp__meta: unknown;
    title: string;
    url: string;
    signpost_content: [SignpostContentProps] | [];
    locale: string;
    show_new_hero: boolean;
    hero_with_media: HeroWithMediaData;
    blocks: CsBlock[];
    page_meta: PageMeta;
  };
  allCsTemplateA: {
    nodes: CsEntryNode[];
  };
};

export const query = graphql`
  query ($id: String!, $uid: String!) {
    csTemplateA(id: { eq: $id }) {
      cslp__meta
      title
      url
      locale
      signpost_content {
        ...SignpostContent
      }
      show_new_hero
      hero_with_media {
        ...HeroWithMedia
      }
      blocks {
        accordion {
          ...AccordionBlockTemplateAPage
        }
        action_cards {
          ...ActionCardsBlock
        }
        contact_cards {
          ...ContactCardsBlock
        }
        content_tabs {
          ...ContentTabsBlock
        }
        cta_banners {
          ...CtaBannersBlock
        }
        detailed_related_content {
          ...DetailedRelatedContentBlock
        }
        document_download_link_list_with_pagination {
          ...DocumentDownloadLinkListWithPaginationBlock
        }
        feature {
          ...FeatureBlock
        }
        feature_video {
          ...FeatureVideoBlock
        }
        image_and_cta_cards {
          ...ImageAndCtaCardsBlock
        }
        image_break {
          ...ImageBreakBlock
        }
        info_banner {
          ...InfoBannerBlock
        }
        info_cards {
          ...InfoCardsBlock
        }
        mini_guide {
          ...MiniGuideBlock
        }
        product_cards {
          ...ProductCardsBlock
        }
        rich_text {
          ...RichTextBlock
        }
        social_media_share {
          ...ShareWidgetBlock
        }
        soundcloud_player {
          ...SoundCloudPlayerBlock
        }
        split_info_card {
          ...SplitInfoCardBlock
        }
        table {
          ...TableBlock
        }
        testimonial {
          ...TestimonialBlock
        }
        testimonial_with_image {
          ...TestimonialWithImageBlock
        }
      }
      page_meta {
        ...PageMeta
      }
    }
    allCsTemplateA(filter: { uid: { eq: $uid } }) {
      nodes {
        cslp__meta
        locale
        page_meta {
          canonical_tag
        }
      }
    }
  }
`;
